import { ModuleDocContent } from "./docsData";

export const learningModuleDocs: Record<string, ModuleDocContent> = {
  "course-catalog": {
    title: "Course Catalog",
    subtitle: "Browse & Manage Courses",
    color: "blue",
    badge: "Course Management",
    description:
      "The central hub for browsing, searching, and managing all courses. Admins manage the catalog with filters, sorting, and view options while learners discover and enroll.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p>The <strong>Course Catalog</strong> is the central interface for discovering and managing courses. It shows aggregate stats at the top, supports advanced filtering by level and stage, and offers both grid and list view modes with multiple sort options.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/8380242d-2f71-4f54-960e-6ceb5fe0883b.png" alt="Admin Course Catalog" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Admin course catalog with status filters</p></div>

      <section>
        <h2>Header Statistics</h2>
        <p>The catalog header displays three summary cards inside a gradient banner:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Total Courses</strong></td>
              <td>Count of all courses in the catalog.</td>
            </tr>
            <tr>
              <td><strong>Published</strong></td>
              <td>Number of courses currently published and visible to learners.</td>
            </tr>
            <tr>
              <td><strong>Draft</strong></td>
              <td>Number of courses saved as draft, not yet published.</td>
            </tr>
          </tbody>
        </table>
        <p>A <strong>Generate Course</strong> button in the header links directly to AI Course Generation.</p>
      </section>

      <section>
        <h2>Search, Filter & Sort</h2>
        <table>
          <thead>
            <tr>
              <th>Control</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Search bar</strong></td>
              <td>Full-text search across course titles with real-time debounced results.</td>
            </tr>
            <tr>
              <td><strong>Level filter</strong></td>
              <td>Filter by difficulty: <code>All</code>, <code>Beginner</code>, <code>Intermediate</code>, <code>Advanced</code>.</td>
            </tr>
            <tr>
              <td><strong>Stage filter</strong></td>
              <td>Filter by publication status: <code>All</code>, <code>Published</code>, <code>Draft</code>, <code>Unpublished</code>.</td>
            </tr>
            <tr>
              <td><strong>View toggle</strong></td>
              <td>Switch between a visual <strong>card grid</strong> and a compact <strong>list view</strong>.</td>
            </tr>
            <tr>
              <td><strong>Sort dropdown</strong></td>
              <td>Options: Recently Updated (default), Newest, Most Popular, Highest Rated, Title A–Z, Title Z–A.</td>
            </tr>
            <tr>
              <td><strong>Clear Filters</strong></td>
              <td>Resets all active filters in one click.</td>
            </tr>
          </tbody>
        </table>
        <p>The Filters panel is collapsible — click the <strong>Filters</strong> button to expand or collapse it.</p>
      </section>

      <section>
        <h2>Course Card</h2>
        <p>Each course card (grid or list view) displays:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Thumbnail</strong></td>
              <td>Course image with a fallback icon if none is set.</td>
            </tr>
            <tr>
              <td><strong>Status badge</strong></td>
              <td>Overlaid on the thumbnail — <code>Published</code>, <code>Draft</code>, or <code>Unpublished</code>.</td>
            </tr>
            <tr>
              <td><strong>Difficulty badge</strong></td>
              <td>Color-coded badge showing the course difficulty level.</td>
            </tr>
            <tr>
              <td><strong>Title</strong></td>
              <td>Clickable link that navigates to the course edit page.</td>
            </tr>
            <tr>
              <td><strong>Description</strong></td>
              <td>Truncated course description (line-clamped).</td>
            </tr>
            <tr>
              <td><strong>Star rating</strong></td>
              <td>Average rating with review count — clickable to view reviews.</td>
            </tr>
            <tr>
              <td><strong>Metadata</strong></td>
              <td>User enrollment count, total duration, and module count.</td>
            </tr>
            <tr>
              <td><strong>Manage button</strong></td>
              <td>Links to the course editor for the selected course.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Finding draft courses for review</div>
            <div class="sz-use-case-flow">
              Set the <strong>Stage</strong> filter to <code>Draft</code> and sort by <strong>Recently Updated</strong> to surface courses awaiting review. Click <strong>Manage</strong> to open the editor and publish.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Browsing top-rated courses</div>
            <div class="sz-use-case-flow">
              Sort by <strong>Highest Rated</strong> to see courses with the highest star ratings first. Use the star rating on each card to compare learner feedback at a glance.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Pro tip</div>
        <p>Use the <strong>Generate Course</strong> button in the header to jump straight to AI Course Generation and instantly start creating a new course.</p>
      </div>

    </div>`,
  },

  "ai-course-gen": {
    title: "AI Course Generation",
    subtitle: "AI-Powered Course Creator",
    color: "blue",
    badge: "Course Management",
    description:
      "Generate complete courses using AI. Describe your course idea, configure delivery options, and the AI creates structured modules with content, assessments, and media automatically.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>AI Course Generation</strong> lets you create full courses from a text description. The AI generates course metadata, structure, images, and lesson content. Advanced options let you configure pedagogy, language, content delivery (HTML, audio, or video), and detailed assessment rules.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/8fec3dc3-e512-4c56-8b21-f4bccde64841.png" alt="AI Course Generation" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: AI course generation configuration</p></div>

      <section>
        <h2>How it works</h2>
        <div class="sz-steps">
          <div class="sz-step">
            <div class="sz-step-num">1</div>
            <div>
              <div class="sz-step-title">Describe your course idea</div>
              <div class="sz-step-desc">
                Enter a course description (100–1,000 characters) in the main textarea. Click <strong>Enhance with AI</strong> to let the AI refine your input for better results. An <strong>Undo</strong> button restores the original text if needed.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">2</div>
            <div>
              <div class="sz-step-title">Set target audience and knowledge store</div>
              <div class="sz-step-desc">
                Specify the <strong>Target Audience</strong> (e.g., "junior developers"). Optionally select a <strong>Knowledge Store</strong> to ground generation in your organization's documents. Toggle <strong>Knowledge Store Only Mode</strong> to restrict AI to your documents, or enable <strong>Example Document Injection</strong> for richer content.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">3</div>
            <div>
              <div class="sz-step-title">Configure advanced settings</div>
              <div class="sz-step-desc">
                Expand <strong>Advanced Settings</strong> to fine-tune difficulty level, content tone, pedagogy framework, language, content delivery type, enhanced features, and assessment rules. All settings have sensible defaults.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">4</div>
            <div>
              <div class="sz-step-title">Generate and track progress</div>
              <div class="sz-step-desc">
                Click <strong>Generate</strong> to start. The course appears in the <strong>Generation History</strong> section below with real-time status (Pending → In Progress → Completed/Failed). A progress bar tracks Metadata, Images, and Course content stages.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2>Advanced Settings</h2>
        <table>
          <thead>
            <tr>
              <th>Section</th>
              <th>Settings</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Course Settings</strong></td>
              <td>Difficulty Level (Beginner / Intermediate / Advanced / Expert), Content Tone (Formal, Professional, Informal, Friendly, Persuasive, Inspirational, etc.), Pedagogy Framework (DEFAULT, ADDIE, Bloom's Taxonomy, Gagné, Kolb's, Constructivist, etc.).</td>
            </tr>
            <tr>
              <td><strong>Course Structure</strong></td>
              <td>Prerequisites (tag input), Total Duration (hours), Target Industry.</td>
            </tr>
            <tr>
              <td><strong>Language Settings</strong></td>
              <td>Language code selection. When applicable: TTS voice configuration (voice type, name, speaking rate, pitch, volume) and Caption configuration (format, min/max duration).</td>
            </tr>
            <tr>
              <td><strong>Content Generation</strong></td>
              <td>Module count (min/max), Content items per module (min/max), Scenes per content item (min/max).</td>
            </tr>
            <tr>
              <td><strong>Content Delivery Type</strong></td>
              <td><code>HTML</code> — text-based lessons. <code>HTML with Audio</code> — lessons with TTS narration. <code>Video Presentation</code> — AI-generated video content.</td>
            </tr>
            <tr>
              <td><strong>Enhanced Features</strong></td>
              <td>Toggles for: Auto-grading, Self-check Quizzes, Adaptive Learning, Analytics, Collaborative Learning, Microlearning.</td>
            </tr>
            <tr>
              <td><strong>Assessment Configuration</strong></td>
              <td>Enable/disable assessments. Question types (Single Choice, Multiple Choice, True/False, Text Answer). Pre/Post assessment toggles per content item, module, and course level. Min/max questions, passing score %, time limit. Options: immediate feedback, allow retry, max attempts, shuffle questions/options, show correct answers.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Generation History</h2>
        <p>Below the form, a paginated grid shows all recent course generations with:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Status badge</strong></td>
              <td>Color-coded: <span style="color:#10b981;">Completed</span>, <span style="color:#3b82f6;">In Progress</span>, <span style="color:#ef4444;">Failed</span>, <span style="color:#6b7280;">Pending</span>.</td>
            </tr>
            <tr>
              <td><strong>Progress bar</strong></td>
              <td>Three colored segments showing completion of Metadata, Images, and Course content stages.</td>
            </tr>
            <tr>
              <td><strong>Course details</strong></td>
              <td>Title, description, creation timestamp, and course count.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Rapid onboarding content</div>
            <div class="sz-use-case-flow">
              Enter a topic like "Engineering Team Onboarding — CI/CD Practices", select your Knowledge Store with internal docs, and generate. The AI creates a course grounded in your own documentation.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Video-based training</div>
            <div class="sz-use-case-flow">
              Set Content Delivery Type to <strong>Video Presentation</strong>, configure TTS voice settings, and enable captions. The AI generates complete video lessons with narration and subtitles.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Pro tip</div>
        <p>Use <strong>Enhance with AI</strong> on your course idea before generating — it produces significantly better course structures. Keep the <strong>Knowledge Store Only Mode</strong> enabled when you need training content strictly based on your internal documents.</p>
      </div>

    </div>`,
  },

  "course-assignment": {
    title: "Course Assignment",
    subtitle: "Assign Courses to Learners",
    color: "blue",
    badge: "Course Assignment",
    description:
      "Assign courses to individuals, departments, or roles. Configure deadlines, notifications, and reminders. Supports bulk import and duplicate detection.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Course Assignment</strong> lets you distribute courses across your organization. Select courses and recipients using four assignment modes, configure deadline and notification settings, and track assignments with duplicate detection.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/faef56f1-56ed-4d1f-9aa6-38b405f4f8be.png" alt="Assign Courses" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Assign courses to learners</p></div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/56c2cd65-5475-46a4-926c-9c56059ac10e.png" alt="Manage Assignments" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Manage course assignments</p></div>

      <section>
        <h2>Assignment Modes</h2>
        <p>Four tabs at the top let you choose the assignment scope:</p>
        <table>
          <thead>
            <tr>
              <th>Mode</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Individual</strong></td>
              <td>Select specific users. Filter by department and role, then search by name. Supports pagination for large user lists.</td>
            </tr>
            <tr>
              <td><strong>Department</strong></td>
              <td>Assign to entire departments. Search and select one or more departments — all current members receive the assignment.</td>
            </tr>
            <tr>
              <td><strong>Role-Based</strong></td>
              <td>Assign by role. Select a department first, then pick roles within it. Supports multi-department role selection.</td>
            </tr>
            <tr>
              <td><strong>Bulk Import</strong></td>
              <td>Upload a CSV file to assign courses in bulk. The system validates entries and reports errors before processing.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>How it works</h2>
        <div class="sz-steps">
          <div class="sz-step">
            <div class="sz-step-num">1</div>
            <div>
              <div class="sz-step-title">Select courses (left panel)</div>
              <div class="sz-step-desc">
                Search and select one or more published courses using checkboxes. Each course card shows a thumbnail, title, module count, difficulty badge, and duration. Only <strong>published courses</strong> are available for assignment.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">2</div>
            <div>
              <div class="sz-step-title">Select recipients (right panel)</div>
              <div class="sz-step-desc">
                Choose an assignment mode tab and select recipients. For Individual mode, filter by department and role to narrow the user list. Selected counts are shown in the panel header.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">3</div>
            <div>
              <div class="sz-step-title">Configure assignment settings</div>
              <div class="sz-step-desc">
                Set <strong>Mandatory Assignment</strong> toggle, <strong>Deadline</strong> (1–365 days), <strong>Send Notification</strong> toggle, <strong>Automatic Reminder</strong> with frequency, and an optional <strong>Custom Message</strong>. <strong>Skip Duplicates</strong> is enabled by default to automatically avoid re-assigning to already-enrolled users.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">4</div>
            <div>
              <div class="sz-step-title">Preview and assign</div>
              <div class="sz-step-desc">
                Click <strong>Preview</strong> to check for duplicate conflicts. The preview modal shows any conflicts found. Click <strong>Assign Courses</strong> to finalize. Assignments appear in the Recent Assignments section below.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2>Recent Assignments</h2>
        <p>A table at the bottom shows the 5 most recent assignments with:</p>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Course(s)</strong></td>
              <td>Names of the assigned courses.</td>
            </tr>
            <tr>
              <td><strong>Type</strong></td>
              <td>Assignment mode used (Individual, Department, Role, Bulk).</td>
            </tr>
            <tr>
              <td><strong>Recipients</strong></td>
              <td>Count and details of assigned users, groups, or roles.</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>Assignment status badge.</td>
            </tr>
            <tr>
              <td><strong>Actions</strong></td>
              <td>Send Reminder, View Details (shows assigned and skipped users), Delete with confirmation.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Department training rollout</div>
            <div class="sz-use-case-flow">
              Switch to the <strong>Department</strong> tab, select the target department, pick the compliance course, set a 30-day deadline with reminders every 7 days. All department members are enrolled automatically.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Role-based skill training</div>
            <div class="sz-use-case-flow">
              Use the <strong>Role-Based</strong> tab to assign technical courses to "Frontend Developer" roles across multiple departments. <strong>Skip Duplicates</strong> is enabled by default, so users who are already enrolled will be automatically skipped.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Pro tip</div>
        <p>Always click <strong>Preview</strong> before assigning to catch duplicate conflicts. Use the <strong>View Details</strong> action on recent assignments to see exactly which users were skipped and why.</p>
      </div>

    </div>`,
  },

  "categories": {
    title: "Category Management",
    subtitle: "Organize Courses by Topic",
    color: "blue",
    badge: "Courses",
    description:
      "Create and manage hierarchical categories to organize your courses. Add, edit, and delete categories with parent-child nesting.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Category Management</strong> lets you organize your courses with hierarchical categories. The page displays a tree-structured list of categories with expand/collapse for nested items, and an <strong>Add Category</strong> button to create new ones.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/59f45e3b-da54-42fd-95a8-a03e06265327.png" alt="Category Management" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Category Management — hierarchical category tree</p></div>

      <section>
        <h2>How it works</h2>
        <div class="sz-steps">
          <div class="sz-step">
            <div class="sz-step-num">1</div>
            <div>
              <div class="sz-step-title">View the category tree</div>
              <div class="sz-step-desc">
                Open <strong>Courses &gt; Categories</strong> from the sidebar. All categories are displayed as a hierarchical tree. Click the arrow icon on any category to expand or collapse its child categories.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">2</div>
            <div>
              <div class="sz-step-title">Add a new category</div>
              <div class="sz-step-desc">
                Click <strong>Add Category</strong> in the top-right corner. In the modal, select a Parent Category (or "None" for a root category), enter a name, and optionally add a description. Click <strong>Create Category</strong>.
              </div>
            </div>
          </div>
          <div class="sz-step">
            <div class="sz-step-num">3</div>
            <div>
              <div class="sz-step-title">Edit or delete categories</div>
              <div class="sz-step-desc">
                Each category row has an <strong>Edit</strong> (pencil) and <strong>Delete</strong> (trash) icon. Edit opens the same modal pre-filled with the category details including its slug. Delete shows a confirmation modal.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2>Category List</h2>
        <p>The page displays all categories as a tree structure:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Category name</strong></td>
              <td>Displayed in each row. Root categories appear at the top level.</td>
            </tr>
            <tr>
              <td><strong>Expand/Collapse</strong></td>
              <td>Arrow icon on categories that have children. Click to show or hide subcategories. Child categories are indented with a left border line.</td>
            </tr>
            <tr>
              <td><strong>Edit button</strong></td>
              <td>Pencil icon &mdash; opens the category modal with pre-filled data.</td>
            </tr>
            <tr>
              <td><strong>Delete button</strong></td>
              <td>Trash icon &mdash; opens a delete confirmation modal.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Add / Edit Category Modal</h2>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Parent Category</strong></td>
              <td>Required. Searchable dropdown. Select "None (Root Category)" for top-level, or pick an existing category to nest under.</td>
            </tr>
            <tr>
              <td><strong>Category Name</strong></td>
              <td>Required. The display name of the category. Must be unique (duplicate names show an error).</td>
            </tr>
            <tr>
              <td><strong>Slug</strong></td>
              <td>Read-only, shown only when editing. URL-friendly version of the name with copy-to-clipboard.</td>
            </tr>
            <tr>
              <td><strong>Description</strong></td>
              <td>Optional textarea for describing the category.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Department-based organization</div>
            <div class="sz-use-case-flow">
              Create root categories per department (Engineering, Sales, HR). Add subcategories for specific skill areas (e.g., Engineering &gt; Frontend, Engineering &gt; DevOps).
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Compliance taxonomy</div>
            <div class="sz-use-case-flow">
              Create a "Compliance" root category with child categories per domain (Data Privacy, Workplace Safety, Information Security). Assign courses to these categories when creating them.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Pro tip</div>
        <p>Keep your category hierarchy shallow &mdash; two levels (parent + child) is usually sufficient for clear organization.</p>
      </div>

    </div>`,
  },

  "trending-topics": {
    title: "AI Trending Topics",
    subtitle: "AI-Suggested Learning Trends",
    color: "blue",
    badge: "Courses",
    description:
      "Discover AI-suggested trending topics based on industry relevance and educational potential. Use insights to identify high-impact course opportunities and build courses directly from topics.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>AI Trending Topics</strong> uses AI to surface high-relevance learning topics for your organization. Each topic is scored for trend strength and educational potential, and includes keywords and related topics to guide content creation. Build new courses directly from any topic with one click.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/7a4ee80a-e8b1-4aad-9398-e63832d7219a.png" alt="Trending Topics" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: AI-suggested trending topics</p></div>

      <section>
        <h2>Header Metrics</h2>
        <p>Three summary metrics are displayed at the top:</p>
        <table>
          <thead>
            <tr>
              <th>Metric</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Trending Now</strong></td>
              <td>Total number of trending topics currently available.</td>
            </tr>
            <tr>
              <td><strong>High Relevance</strong></td>
              <td>Count of topics rated as Critical or High relevance.</td>
            </tr>
            <tr>
              <td><strong>Avg Educational Potential</strong></td>
              <td>Average educational potential score across all topics as a percentage.</td>
            </tr>
          </tbody>
        </table>
        <p>Click <strong>Discover New Topics</strong> (with Sparkles icon) to trigger a new AI discovery scan.</p>
      </section>

      <section>
        <h2>Topic Cards</h2>
        <p>Topics are displayed in a two-column card grid. Each card shows:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Title &amp; Category</strong></td>
              <td>Topic name with its category label and last-updated date.</td>
            </tr>
            <tr>
              <td><strong>Relevance badge</strong></td>
              <td>Color-coded: <span style="color:#ef4444;">Critical</span>, <span style="color:#f59e0b;">High</span>, <span style="color:#3b82f6;">Medium</span>, <span style="color:#6b7280;">Low</span>.</td>
            </tr>
            <tr>
              <td><strong>Trend Score</strong></td>
              <td>A large numeric score reflecting overall trend strength, color-coded by intensity.</td>
            </tr>
            <tr>
              <td><strong>Educational Potential</strong></td>
              <td>Progress bar showing the topic's educational value as a percentage.</td>
            </tr>
            <tr>
              <td><strong>Keywords</strong></td>
              <td>Tag chips showing the first 5 keywords. Click "+X more" to view all in a modal.</td>
            </tr>
            <tr>
              <td><strong>Related Topics</strong></td>
              <td>Tag chips showing related topics. Click "+X more" to view all in a modal.</td>
            </tr>
            <tr>
              <td><strong>Build Course</strong></td>
              <td>Button that navigates directly to AI Course Generation pre-filled with the topic.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Search &amp; Sort</h2>
        <table>
          <thead>
            <tr>
              <th>Control</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Search</strong></td>
              <td>Filter topics by title, description, or keywords.</td>
            </tr>
            <tr>
              <td><strong>Sort by</strong></td>
              <td>Options: Trend Score, Educational Potential, Relevance, Name.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Content planning</div>
            <div class="sz-use-case-flow">
              Review trending topics monthly. Identify topics with <strong>Critical</strong> or <strong>High</strong> relevance and high educational potential. Click <strong>Build Course</strong> to create AI-generated courses for the most impactful topics.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Identifying skill gaps</div>
            <div class="sz-use-case-flow">
              Sort by <strong>Trend Score</strong> to find emerging topics your catalog doesn't cover. Use the keywords and related topics on each card to understand the scope before creating new courses.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Pro tip</div>
        <p>Click <strong>Discover New Topics</strong> periodically to refresh the AI suggestions. Focus on topics with both high Trend Score and high Educational Potential for the biggest training impact.</p>
      </div>

    </div>`,
  },
};
